(() => {
  if (window.SuncoMainSlideshowLoaded) return;
  window.SuncoMainSlideshowLoaded = true;

  const instances = new WeakMap();

  class SuncoMainSlideshow {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('.sunco-main-slideshow__track');
      this.slides = Array.from(root.querySelectorAll('[data-sunco-main-slide]'));
      this.dots = Array.from(root.querySelectorAll('[data-sunco-main-dot]'));
      this.previousButton = root.querySelector('[data-sunco-main-previous]');
      this.nextButton = root.querySelector('[data-sunco-main-next]');
      this.currentIndex = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
      this.transition = root.dataset.transition || 'fade';
      this.autoplay = root.dataset.autoplay === 'true' && this.slides.length > 1;
      this.autoplaySpeed = Number(root.dataset.autoplaySpeed || 5000);
      this.pauseOnHover = root.dataset.pauseOnHover === 'true';
      this.mobileQuery = window.matchMedia('(max-width: 749px)');
      this.timer = null;

      this.bind();
      this.goTo(this.currentIndex, { restart: false });
      this.start();
    }

    bind() {
      this.previousButton?.addEventListener('click', () => this.goTo(this.currentIndex - 1));
      this.nextButton?.addEventListener('click', () => this.goTo(this.currentIndex + 1));

      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => this.goTo(Number(dot.dataset.suncoMainDot || 0)));
      });

      this.root.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.goTo(this.currentIndex - 1);
        }
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.goTo(this.currentIndex + 1);
        }
      });

      if (this.pauseOnHover) {
        this.root.addEventListener('mouseenter', () => this.stop());
        this.root.addEventListener('mouseleave', () => this.start());
        this.root.addEventListener('focusin', () => this.stop());
        this.root.addEventListener('focusout', () => this.start());
      }

      const ratioUpdate = () => this.updateRatio();
      if (this.mobileQuery.addEventListener) {
        this.mobileQuery.addEventListener('change', ratioUpdate);
      } else {
        this.mobileQuery.addListener(ratioUpdate);
      }
    }

    normalizeIndex(index) {
      if (!this.slides.length) return 0;
      return (index + this.slides.length) % this.slides.length;
    }

    goTo(index, options = {}) {
      if (!this.slides.length) return;
      const nextIndex = this.normalizeIndex(index);
      const nextSlide = this.slides[nextIndex];
      const useDepthCrossfade = this.transition === 'depth-crossfade' && nextIndex !== this.currentIndex;

      this.slides.forEach((slide) => slide.classList.remove('is-entering'));
      if (useDepthCrossfade) {
        nextSlide.classList.add('is-entering');
      }

      this.currentIndex = nextIndex;
      this.root.style.setProperty('--sunco-main-slideshow-index', this.currentIndex);

      this.slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === this.currentIndex;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        this.updateVideoState(slide, isActive);
      });

      if (useDepthCrossfade) {
        nextSlide.getBoundingClientRect();
        nextSlide.classList.remove('is-entering');
      }

      this.dots.forEach((dot, dotIndex) => {
        const isActive = dotIndex === this.currentIndex;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });

      this.updateRatio();

      if (options.restart !== false) {
        this.restart();
      }
    }

    updateRatio() {
      const slide = this.slides[this.currentIndex];
      if (!slide) return;

      const desktopRatio = slide.dataset.ratioDesktop || '2.611111';
      const mobileRatio = slide.dataset.ratioMobile || desktopRatio;
      this.root.style.setProperty('--sunco-main-slideshow-active-ratio', desktopRatio);
      this.root.style.setProperty('--sunco-main-slideshow-active-ratio-mobile', mobileRatio);
    }

    updateVideoState(slide, isActive) {
      slide.querySelectorAll('video').forEach((video) => {
        if (!isActive || video.offsetParent === null) {
          video.pause();
          return;
        }

        video.play().catch(() => {});
      });
    }

    start() {
      if (!this.autoplay || this.timer) return;
      this.timer = window.setInterval(() => this.goTo(this.currentIndex + 1, { restart: false }), this.autoplaySpeed);
    }

    stop() {
      if (!this.timer) return;
      window.clearInterval(this.timer);
      this.timer = null;
    }

    restart() {
      this.stop();
      this.start();
    }

    destroy() {
      this.stop();
    }
  }

  const init = (root = document) => {
    root.querySelectorAll('[data-sunco-main-slideshow]').forEach((section) => {
      if (instances.has(section)) return;
      instances.set(section, new SuncoMainSlideshow(section));
    });
  };

  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-sunco-main-slideshow]').forEach((section) => {
      const instance = instances.get(section);
      instance?.destroy();
      instances.delete(section);
    });
  });
  document.addEventListener('shopify:block:select', (event) => {
    const section = event.target.closest('[data-sunco-main-slideshow]');
    const instance = section && instances.get(section);
    if (!instance) return;

    const slideIndex = instance.slides.findIndex((slide) => slide.dataset.blockId === event.detail.blockId);
    if (slideIndex >= 0) {
      instance.goTo(slideIndex);
      instance.stop();
    }
  });
  document.addEventListener('shopify:block:deselect', (event) => {
    const section = event.target.closest('[data-sunco-main-slideshow]');
    const instance = section && instances.get(section);
    instance?.start();
  });
})();
